import { GetStaticPaths, GetStaticPathsResult } from 'next';

/**
 * Static paths for the venue pages. Nothing is prerendered by default, pages are rendered on demand.
 * @see https://nextjs.org/docs/basic-features/data-fetching#getstaticpaths-static-generation
 * @see https://nextjs.org/docs/basic-features/data-fetching#fallback-pages
 *
 * If you want to extend the static paths array, you can do like below example:
 * @example
 * export const getStaticPaths = getStaticVenuePaths(
 *   async ({ locales }): Promise<GetStaticPathsResult> => {
 *     return {
 *       paths: locales.map((locale) => ({
 *         params: {
 *           venueId: 'test-venue',
 *         },
 *         locale,
 *       })),
 *       fallback: false,
 *     };
 *   },
 * );
 */
const getStaticVenuePaths = (gsp?: GetStaticPaths | null | undefined) =>
  async function (context): Promise<GetStaticPathsResult> {
    const gspResult: GetStaticPathsResult = gsp ? await gsp(context) : null;

    // No venue pages are prerendered at build time: every venue × locale × page would be
    // thousands of pages and makes each deploy take ~15 minutes. With `fallback: 'blocking'`
    // each page is rendered on its first request and then cached (ISR via `revalidate`).
    // Unknown venues return `notFound` from `withVenue`.
    return {
      paths: gspResult?.paths || [],
      fallback: gspResult?.fallback ?? 'blocking',
    };
  };

export default getStaticVenuePaths;
